import { Breadcrumb, notification } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  CHECKOUT_STEP,
  KEY_SHOPPING_CART,
  ORDER_STATUS_FAIL,
  ORDER_STATUS_SUCCESS,
  PAYMENT_METHODS_NEED_VERIFY_OTP,
  PAYMENT_METHOD_SC,
  ROUTER_PATH,
  VERIFY_OTP_ERROR_CODES,
} from "../../common/constant";
import { ICheckoutProps } from "../../models/interface/ICheckoutProps";
import IShippingInfo from "../../models/shippingInfoModel";
import { localizeRouteKey, renderTotalAmount } from "../../shared/helpers";
import { setTitlePage } from "../../utils/helper";
import ShippingInfo from "./shippingInfo";
import { addShippingInfo } from "../../redux/reducers/slice/profile";
import "./style.scss";
import PaymentMethod from "./paymentMethod";
import {
  actionClearShoppingCart,
  selectCart,
  setCaptchaToken,
  setInvoiceInfo,
  setOTPInfo,
  setPaymentMethod,
} from "../../redux/reducers/slice/cart";
import ShoppingCartInfo from "./shoppingCartInfo";
import { ShoppingCartOutlined } from "@ant-design/icons";
import OrderVoucherCode from "../../components/orderVoucherCode";
import {
  getVerifyOTPInfo,
  submitOrder,
  validateCart,
} from "../../services/shoppingCart";
import AlertModal from "./orderAlertModal";
import * as GTM from "../../components/tracking/GTM";
import { IShoppingCart } from "../../models/IShoppingCartModel";
import VerifyOTPModal from "./verifyOTPModal";
import { getUserInfoLocal } from "../../services/profile";
import moment from "moment";

const Checkout = (props: ICheckoutProps) => {
  const { history, t, match } = props;
  const [hide, setHide] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [step, setStep] = useState<number>(1);
  const [isBeginCheckout, setIsBeginCheckout] = useState<boolean>(false);
  const urlPaymentMethod = localizeRouteKey(ROUTER_PATH.CHECKOUT, [
    t(CHECKOUT_STEP.PAYMENT_METHOD),
  ]);
  const urlShippingInfo = localizeRouteKey(ROUTER_PATH.CHECKOUT, [
    t(CHECKOUT_STEP.SHIPPING_INFO),
  ]);
  const [orderResponse, setOrderResponse] = useState<any>({});
  const [typeAlert, setTypeAlert] = useState<string>(ORDER_STATUS_SUCCESS);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [formSc, setFormSC] = useState("");

  const otpModalRef: React.Ref<any> = useRef(null);

  const carts: IShoppingCart = useSelector(selectCart);
  React.useEffect(() => {
    setTitlePage(t("checkout_title"));
  }, []);

  useEffect(() => {
    if (!carts || !carts.items || carts.items.length == 0) {
      history.replace(localizeRouteKey(ROUTER_PATH.HOME));
    }

    if (match?.params?.step) {
      if (t(CHECKOUT_STEP.SHIPPING_INFO) == match?.params?.step) {
        setStep(1);
      } else if (t(CHECKOUT_STEP.PAYMENT_METHOD) == match?.params?.step) {
        setStep(2);
      } else {
        history.replace(localizeRouteKey(ROUTER_PATH.HOME));
      }
    }
  }, [match]);

  useEffect(() => {
    if (carts?.items && step && !isBeginCheckout) {
      const products = carts.items.map((sci) => {
        let unit_price = sci.product?.feature?.price_promotion
          ? parseInt(sci.product?.feature?.price_promotion?.replaceAll(".", ""))
          : 0;
        return {
          name: sci.product.name,
          id: sci.product.code,
          price: sci.product?.feature?.price
            ? parseInt(sci.product?.feature?.price?.replaceAll(".", ""))
            : 0,
          category: sci.product?.category_name,
          variant: sci.product?.feature?.spec,
          quantity: sci.quantity,
          promotion: "",
          discount_price: sci.product?.feature?.price_save
            ? parseInt(sci.product?.feature?.price_save.replaceAll(".", ""))
            : 0,
          unit_price: unit_price,
          subtotal_amount: unit_price * sci.quantity,
        };
      });
      GTM.beginCheckout(products);
      setIsBeginCheckout(true);
    }
  }, [step]);

  useEffect(() => {
    if (formSc) {
      const frm: any = document.getElementById("s2bpay-bcollect-form");
      if (frm && frm.submit) {
        frm.submit();
      }
    }
  }, [formSc]);

  const goToPaymentMethod = (info: IShippingInfo) => {
    dispatch(addShippingInfo(info));
    if (carts && carts.items) {
      const total = carts.total_amount
        ? parseInt(carts.total_amount.replaceAll(".", ""))
        : 0;
      const products = carts.items.map((sci) => {
        let unit_price = sci.product?.feature?.price_promotion
          ? parseInt(sci.product?.feature?.price_promotion?.replaceAll(".", ""))
          : 0;
        return {
          name: sci.product.name,
          id: sci.product.code,
          price: sci.product?.feature?.price
            ? parseInt(sci.product?.feature?.price?.replaceAll(".", ""))
            : 0,
          category: sci.product?.category_name,
          variant: sci.product?.feature?.spec,
          quantity: sci.quantity,
          promotion: "",
          discount_price: sci.product?.feature?.price_save
            ? parseInt(sci.product?.feature?.price_save.replaceAll(".", ""))
            : 0,
          unit_price: unit_price,
          subtotal_amount: unit_price * sci.quantity,
        };
      });

      GTM.checkout(GTM.ECheckoutType.SHIPPING_INFO, products, total);
    }
    history.push(urlPaymentMethod);
  };

  const checkOrderNeedOTP = (info: any) => {
    const user = getUserInfoLocal();
    const logined = user != undefined && user != null;
    const checkPaymentMethodsNeedVerifyOTP =
      PAYMENT_METHODS_NEED_VERIFY_OTP.includes(
        info?.payment_method?.payment_method
      );

    if (checkPaymentMethodsNeedVerifyOTP && !logined) {
      return true;
    }
    return false;
  };

  const calcExpireTime = (expireAt: string = "") => {
    const now = moment().toISOString();
    const diffTime = moment(expireAt).diff(now, "s");

    if (diffTime <= 0) {
      return null;
    }
    return diffTime;
  };

  const checkPaymentMethod = (info: any) => {
    const carts = localStorage.getItem(KEY_SHOPPING_CART);
    let shoppingCart: IShoppingCart = carts ? JSON.parse(carts) : null;

    if (checkOrderNeedOTP(info)) {
      let parseOTPInfo = getVerifyOTPInfo(
        shoppingCart?.shipping_info?.phone_number
      );

      if (
        info.payment_method.payment_method !=
        shoppingCart?.payment_method?.payment_method
      ) {
        dispatch(setPaymentMethod(info.payment_method));
      }

      if (parseOTPInfo?.id && calcExpireTime(parseOTPInfo?.expire_at)) {
        otpModalRef.current.openModal(parseOTPInfo, info);
      } else {
        otpModalRef.current.openModal({}, info);
      }
    } else {
      gotoComplete({
        ...info,
        otp_verify: { otp: null, expire_at: null, id: null },
      });
    }
  };

  const gotoComplete = (info: any) => {
    if (info.payment_method) {
      dispatch(setPaymentMethod(info.payment_method));
    }
    if (info.token) {
      dispatch(setCaptchaToken(info.token));
    }
    if (info.invoice_info) {
      dispatch(setInvoiceInfo(info.invoice_info));
    }
    if (info.otp) {
      dispatch(setOTPInfo({ otp: info.otp }));
    }
    if (info.otp_verify) {
      dispatch(setOTPInfo(info.otp_verify));
    }
    if (info.otp_method) {
      dispatch(setOTPInfo({ otp_method: info.otp_method }));
    }
    if (carts && carts.items) {
      const total = carts.total_amount
        ? parseInt(carts.total_amount.replaceAll(".", ""))
        : 0;
      const products = carts.items.map((sci) => {
        let unit_price = sci.product?.feature?.price_promotion
          ? parseInt(sci.product?.feature?.price_promotion?.replaceAll(".", ""))
          : 0;
        return {
          name: sci.product.name,
          id: sci.product.code,
          price: sci.product?.feature?.price
            ? parseInt(sci.product?.feature?.price?.replaceAll(".", ""))
            : 0,
          category: sci.product?.category_name,
          variant: sci.product?.feature?.spec,
          quantity: sci.quantity,
          promotion: "",
          discount_price: sci.product?.feature?.price_save
            ? parseInt(sci.product?.feature?.price_save.replaceAll(".", ""))
            : 0,
          unit_price: unit_price,
          subtotal_amount: unit_price * sci.quantity,
        };
      });

      GTM.checkout(
        GTM.ECheckoutType.PAYMENT_METHOD,
        products,
        total,
        info.payment_method
      );
    }

    handleSubmitOrder(info);
  };

  const handleOnClickToggle = (value: boolean) => {
    setHide(value);
  };

  const handleSubmitOrder = async (info: any) => {
    const result = await validateCart();
    if (result.isValid) {
      if (result.messageCode == "product_out_of_stock") {
        notification.warning({
          message: t(result.messageCode),
          description: result.message,
        });
      } else {
        notification.error({
          message: t(result.messageCode),
        });
      }
      return;
    }
    let dataTracking: GTM.IOrderTracking = {
      orderInfo: {
        order_id: "",
        total: 0,
        voucher_code: "",
      },
      products: [],
    };
    if (carts && carts.items) {
      dataTracking.orderInfo.total = carts.total_amount
        ? parseInt(carts.total_amount.replaceAll(".", ""))
        : 0;
      dataTracking.orderInfo.voucher_code = carts.order_voucher_code
        ? carts.order_voucher_code
        : "";
      dataTracking.products = carts.items.map((sci) => {
        let unit_price = sci.product?.feature?.price_promotion
          ? parseInt(sci.product?.feature?.price_promotion?.replaceAll(".", ""))
          : 0;
        let product: GTM.IProductTrackingInfo = {
          name: sci.product.name,
          id: sci.product.code,
          price: sci.product?.feature?.price
            ? parseInt(sci.product?.feature?.price?.replaceAll(".", ""))
            : 0,
          category: "",
          quantity: sci.quantity,
          promotion: "",
          discount_price: sci.product?.feature?.price_save
            ? parseInt(sci.product?.feature?.price_save.replaceAll(".", ""))
            : 0,
          unit_price: unit_price,
          subtotal_amount: unit_price * sci.quantity,
        };
        return product;
      });
    }
    const hasValidateOTP = checkOrderNeedOTP(info);
    submitOrder((data: any, err: any) => {
      if (err) {
        setErrorMessage(err.message || "");
        setTypeAlert(ORDER_STATUS_FAIL);
        setShowAlert(true);
        return;
      }

      if (hasValidateOTP) {
        let otpInfo = {};
        if (data?.expire_at && data?.id) {
          otpInfo = {
            expire_at: data.expire_at,
            id: data.id,
            is_send_zalo_success: data?.is_send_zalo_success,
            otp_method: data?.otp_method,
          };
        }
        if (
          data?.code &&
          data.code === VERIFY_OTP_ERROR_CODES.VERIFY_OTP_NOT_EXPIRED
        ) {
          otpInfo = {
            expire_at: data?.data?.expire_at,
            id: data?.data?.id,
            is_send_zalo_success: data?.is_send_zalo_success,
            otp_method: data?.otp_method,
          };
        }

        dispatch(setOTPInfo(otpInfo));
        otpModalRef.current.openModal({ ...data, ...otpInfo });
      } else if (data && data.code && VERIFY_OTP_ERROR_CODES[data.code]) {
        otpModalRef.current.handleVerifyOTP(data);
      } else if (data && data.payment_method === PAYMENT_METHOD_SC) {
        setFormSC(data.html);
      } else if (data.expire_at && data.id) {
        otpModalRef.current.handleVerifyOTP(data);
        dispatch(setOTPInfo({ expire_at: data.expire_at, id: data.id }));
      } else if (data) {
        otpModalRef.current.closeModal();
        dataTracking.orderInfo.order_id = data.order_id;
        setOrderResponse(data);
        setTypeAlert(ORDER_STATUS_SUCCESS);
        setShowAlert(true);
      } else {
        setErrorMessage(err.message || "");
        setTypeAlert(ORDER_STATUS_FAIL);
        setShowAlert(true);
      }
    });
  };

  return (
    <div
      className={`checkout-container ${hide && "hide-shopping-cart"}`}
      id="checkout-container">
      <VerifyOTPModal ref={otpModalRef} gotoComplete={gotoComplete} />

      <AlertModal
        {...props}
        isVisible={showAlert}
        orderResponse={orderResponse}
        typeAlert={typeAlert}
        handleClose={() => {
          dispatch(actionClearShoppingCart());
          history.push(localizeRouteKey(ROUTER_PATH.HOME));
          setShowAlert(false);
        }}
        errorMessage={errorMessage}
      />

      <div className="order-summary-toggle order-summary-toggle-hide">
        <div className="order-summary-toggle-inner">
          <div className="order-summary-toggle-icon-wrapper">
            <ShoppingCartOutlined style={{ fontSize: "20px" }} />
            {hide ? (
              <div
                className="order-summary-toggle-text order-summary-toggle-text-show"
                onClick={() => handleOnClickToggle(false)}>
                <span>{t("checkout_show_shopping_cart")}</span>
                <svg
                  width="11"
                  height="6"
                  xmlns="http://www.w3.org/2000/svg"
                  className="order-summary-toggle-dropdown"
                  fill="#000">
                  <path d="M.504 1.813l4.358 3.845.496.438.496-.438 4.642-4.096L9.504.438 4.862 4.534h.992L1.496.69.504 1.812z"></path>
                </svg>
              </div>
            ) : (
              <div
                className="order-summary-toggle-text order-summary-toggle-text-hide"
                onClick={() => handleOnClickToggle(true)}>
                <span>{t("checkout_hide_shopping_cart")}</span>
                <svg
                  width="11"
                  height="7"
                  xmlns="http://www.w3.org/2000/svg"
                  className="order-summary-toggle-dropdown"
                  fill="#000">
                  <path d="M6.138.876L5.642.438l-.496.438L.504 4.972l.992 1.124L6.138 2l-.496.436 3.862 3.408.992-1.122L6.138.876z"></path>
                </svg>
              </div>
            )}
          </div>
          <div className="order-summary-toggle-total-recap">
            <span className="total-recap-final-price">
              {renderTotalAmount(carts?.total_amount)}
            </span>
          </div>
        </div>
      </div>
      <div className="order-summary-toggle order-summary-toggle-hide">
        <div className="order-summary-toggle-inner">
          <OrderVoucherCode />
        </div>
      </div>
      <div className="checkout-warp">
        <div className="checkout-carts-container">
          <ShoppingCartInfo carts={carts} hide={hide} />
        </div>
        <div className="checkout-info-container">
          <div className="checkout-info-header">
            <Breadcrumb separator=">">
              <Breadcrumb.Item>
                <Link
                  to={localizeRouteKey(ROUTER_PATH.CART)}
                  className={"checkout-breadcrumb-active"}>
                  {t("shopping_cart_title")}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {step == 1 ? (
                  <span className={"checkout-breadcrumb-active"}>
                    {t("shipping_info_title")}
                  </span>
                ) : (
                  <Link
                    to={urlShippingInfo}
                    className={"checkout-breadcrumb-active"}>
                    {t("shipping_info_title")}
                  </Link>
                )}
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                {step == 2 ? (
                  <span className={"checkout-breadcrumb-active"}>
                    {t("payment_method_title")}
                  </span>
                ) : (
                  t("payment_method_title")
                )}
              </Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <div>
            {step == 1 ? (
              <ShippingInfo
                history={history}
                t={t}
                goToPaymentMethod={(info: IShippingInfo) =>
                  goToPaymentMethod(info)
                }
              />
            ) : null}
            {step == 2 ? (
              <PaymentMethod
                history={history}
                t={t}
                gotoComplete={(value: any) => checkPaymentMethod(value)}
              />
            ) : null}
          </div>
          <div dangerouslySetInnerHTML={{ __html: formSc }}></div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(Checkout);
